//* Library imports
import { motion } from "framer-motion";

//* Component imports
import { Loading } from "../../../../../../component/common/Loading";
import { ProcessEssayActions } from "./process-essay-actions";
  
type EssayField = {
  name: string;
  totalQuestions: string;
  header?: string;
  type?: string;
};

type ProcessEssayTableProps = {
  fields: EssayField[];
  items: any[];
  loading: boolean;
  notFoundMessage: string;
  onEditResult?: (id: string) => void;
  onDeleteResult?: (id: string) => void;
  onViewResult?: (id: number) => void;
  onViewExamResults?: (id: number) => void;
  hideEdit?: boolean;
  hideDelete?: boolean;
  hideView?: boolean;
  hideResults?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
};

export function ProcessEssayTable({
  fields,
  items,
  loading,
  notFoundMessage,
  onViewResult,
  onViewExamResults,
  hideView = false,
  hideResults = false,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,  
  onPageChange,
}: ProcessEssayTableProps) {

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Loading data-testid="loading-spinner" />
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="text-center py-5 my-4"
      >
        <div className="text-muted">
          <div className="mb-4">
            <i className="fas fa-inbox fa-3x text-secondary opacity-50"></i>
          </div>
          <h5 className="fw-normal text-secondary mb-0">{notFoundMessage}</h5>
        </div>
      </motion.div>
    );
  }

  const renderPagination = () => {
    if (!onPageChange || totalPages <= 1) return null;
    const startItem = (currentPage - 1) * 10 + 1;
    const endItem = Math.min(currentPage * 10, totalItems);
    return (
      <div className="mt-4 d-flex justify-content-between align-items-start p-3 bg-light rounded-3">
        <p className="small text-muted mb-0" style={{ fontSize: "0.875rem" }}>
          Mostrando <span className="fw-semibold text-dark">{startItem}</span> a{" "}
          <span className="fw-semibold text-dark">{endItem}</span> de{" "}
          <span className="fw-semibold text-dark">{totalItems}</span> resultados
        </p>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary btn-sm px-4 py-2 rounded-pill"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            style={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            <i className="fas fa-chevron-left me-1" />
            Anterior
          </button>
          <button
            className="btn btn-outline-primary btn-sm px-4 py-2 rounded-pill"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            style={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            Próximo
            <i className="fas fa-chevron-right ms-1" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="table-responsive shadow-sm rounded-3 border-0"
        style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
      >
        <table className="table table-hover mb-0 text-center align-middle">
          <thead className="bg-light border-0">
            <tr>
              {fields.map((field, idx) => (
                <th
                  key={idx}
                  className={`px-4 py-4 border-0 fw-semibold text-dark align-middle ${
                    idx === 0 ? "text-start" : "text-center"
                  }`}
                  style={{ fontSize: "0.875rem", letterSpacing: "0.025em" }}
                >
                  {field.header || field.name}
                </th>
              ))}
              <th
                className="px-4 py-4 border-0 fw-semibold text-dark align-middle text-center"
                style={{
                  fontSize: "0.875rem",
                  letterSpacing: "0.025em",
                  width: 120,
                }}
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, rowIndex) => (
              <motion.tr
                key={rowIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: rowIndex * 0.05, duration: 0.3 }}
                className="border-0"
                style={{ borderBottom: "1px solid #f1f3f4" }}
              >
                {fields.map((field, colIndex) => {
                  const isFirst = colIndex === 0;
                  const baseClasses = `px-4 py-4 border-0 align-middle ${
                    isFirst ? "text-start" : "text-center"
                  }`;
                  const fontSize = isFirst ? "0.9rem" : "0.875rem";                  return (
                    <td
                      key={colIndex}
                      className={baseClasses}
                      style={{ fontSize }}
                    >
                      <span>
                        {item[field.name as keyof typeof item]?.toString() ??
                          "N/A"}
                      </span>
                    </td>
                  );
                })}                  
                <td className="px-4 py-4 text-center align-middle border-0">
                  <ProcessEssayActions
                    examId={item.id}
                    textoRedacaoId={item.textoRedacaoId}
                    correctionStatus={item.correctionStatus}
                    onViewClick={
                      onViewResult ? () => onViewResult(item.id) : undefined
                    }
                    onResultsClick={
                      onViewExamResults 
                        ? (textoRedacaoId: number) => onViewExamResults(textoRedacaoId)
                        : undefined
                    }
                    hideView={hideView}
                    hideResults={hideResults}
                  />
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </>
  );
}
